import { makeRoute } from "@flow/common/route";
import * as Safe from "safe-portals";
import {
  submissionListItemSerializer,
  settingsSerializer,
  responsesListItemSerializer,
  selectedResponsesListItemSerializer,
  emailStatsListItemSerializer,
} from "./types";
import { Serializers } from "@flow/common/types";
import * as perm from "@flow/common/core_permissions";

export const submitMemberEmail = makeRoute(
  "/api/plugin/send_a_message/submit_member_email",
  "public",
  Safe.obj({
    context: Serializers.journeyContext,
    actionGuid: Safe.str,
    action_variant_idx: Safe.int,
    use_prefill_member_data: Safe.bool,
    campaignTargetUniqueIds: Safe.array(Safe.str),
    subject: Safe.str,
    message: Safe.str,
    consents: Safe.array(Serializers.consent),
    captchaToken: Safe.optional(Safe.str),
    testMode: Safe.bool,
  }),
  Safe.nothing
);

export const submitMemberVideo = makeRoute(
  "/api/plugin/send_a_message/submit_member_video",
  "public",
  Safe.obj({
    context: Serializers.journeyContext,
    actionGuid: Safe.str,
    action_variant_idx: Safe.int,
    use_prefill_member_data: Safe.bool,
    campaignTargetUniqueIds: Safe.array(Safe.str),
    message: Safe.obj({
      introduction: Safe.str,
    }),
    mimeType: Safe.str,
    consents: Safe.array(Serializers.consent),
    captchaToken: Safe.optional(Safe.str),
    testMode: Safe.bool,
  }),
  // response
  Safe.optional(Safe.obj({ url: Safe.str, fields: Safe.raw }))
);

export const getSettings = makeRoute(
  "/admin/api/send_a_message/get_settings",
  [perm.VIEW_SETTINGS],
  Safe.nothing,
  settingsSerializer
);

export const setSettings = makeRoute(
  "/admin/api/send_a_message/set_settings",
  [perm.UPDATE_SETTINGS],
  settingsSerializer,
  Safe.nothing
);

export const listSubmissions = makeRoute(
  "/admin/api/plugin/send_a_message/list_submissions",
  [],
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    actionGuid: Safe.uuid,
    status: Safe.optional(Safe.str),
    type: Safe.optional(Safe.str),
    search: Safe.optional(Safe.str),
    target_name: Safe.str,
    selection: Safe.array(Safe.str),
  }),
  Safe.array(submissionListItemSerializer)
);

export const getTargetResponseConsentConfigs = makeRoute(
  "/api/plugin/send_a_message/get_target_response_consent_configs",
  "public",
  Safe.nothing,
  Safe.array(Serializers.consentConfig)
);

export const submitTargetResponse = makeRoute(
  "/api/plugin/send_a_message/submit_response",
  "public",
  Safe.obj({
    actionGuid: Safe.uuid,
    whoResponded: Safe.str,
    response: Safe.str,
    decisionMakerSupport: Safe.str,
    userEmail: Safe.str,
    consents: Safe.array(Serializers.consent),
  }),
  Safe.nothing
);

export const listResponses = makeRoute(
  "/admin/api/plugin/send_a_message/responses",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    actionGuid: Safe.optional(Safe.str),
  }),
  // response
  Safe.array(responsesListItemSerializer)
);

export const listEmailStats = makeRoute(
  "/admin/api/plugin/send_a_message/stats",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    actionGuid: Safe.str,
  }),
  // response
  Safe.array(emailStatsListItemSerializer)
);

export const getResponses = makeRoute(
  "/admin/api/plugin/send_a_message/response",
  [],
  // arguments
  Safe.obj({
    page: Safe.int,
    rowsPerPage: Safe.int,
    actionGuid: Safe.str,
    selection: Safe.array(Safe.str),
    decisionMakerSupport: Safe.optional(Safe.str),
    search: Safe.optional(Safe.str),
  }),
  // response
  Safe.array(selectedResponsesListItemSerializer)
);

export const getTargets = makeRoute(
  "/admin/api/plugin/send_a_message/targets",
  [],
  // arguments
  Safe.obj({ actionGuid: Safe.str }),
  // response
  Safe.array(Safe.str)
);

export const downloadResponses = makeRoute(
  "/admin/api/plugin/send_a_message/download_responses",
  [],
  Safe.obj({
    actionGuid: Safe.str,
  }),
  Safe.optional(Safe.str)
);
export const downloadSubmissions = makeRoute(
  "/admin/api/plugin/send_a_message/download_submissions",
  [],
  Safe.obj({
    actionGuid: Safe.str,
  }),
  Safe.str
);

export const getSubmissionTargets = makeRoute(
  "/admin/api/plugin/send_a_message/submissions/targets",
  [],
  // arguments
  Safe.obj({ actionGuid: Safe.str }),
  // response
  Safe.array(Safe.obj({ unique_target_id: Safe.str, target_name: Safe.str }))
);

export const tryResendSubmission = makeRoute(
  "/admin/api/plugin/send_a_message/try_resend_submission",
  [],
  Safe.obj({
    submission_guid: Safe.uuid,
  }),
  Safe.nothing
);

export const moderateSubmissionsAsAccepted = makeRoute(
  "/admin/api/plugin/send_a_message/moderate_submissions_as_accepted",
  [],
  Safe.obj({
    submission_guids: Safe.array(Safe.uuid),
  }),
  Safe.nothing
);

export const moderateSubmissionsAsRejected = makeRoute(
  "/admin/api/plugin/send_a_message/moderate_submissions_as_rejected",
  [],
  Safe.obj({
    submission_guids: Safe.array(Safe.uuid),
  }),
  Safe.nothing
);

export const handleAcceptAll = makeRoute(
  "/admin/api/plugin/send_a_message/accept_all_unmoderated",
  [],
  Safe.nothing,
  Safe.nothing
);

export const renderReminderEmailPreview = makeRoute(
  "/admin/api/plugin/send_a_message/reminder_email_preview",
  [],
  Safe.obj({
    from: Safe.str,
    subject: Safe.str,
    body: Safe.str,
    footer: Safe.str,
    actionTitle: Safe.str,
    memberName: Safe.str,
    responseUrl: Safe.str,
    sentMessage: Safe.str,
    template: Safe.optional(Safe.str),
  }),
  Safe.Result.serializer({
    ok: Safe.obj({ subject: Safe.str, body: Safe.str }),
    error: Safe.str,
  })
);

export const renderVideoEmailPreview = makeRoute(
  "/admin/api/plugin/send_a_message/video_email_preview",
  [],
  Safe.obj({
    footer: Safe.str,
    introduction: Safe.str,
    memberEmail: Safe.str,
    memberName: Safe.str,
    memberPostcode: Safe.str,
    line1: Safe.str,
    memberTown: Safe.str,
    targetName: Safe.str,
    thumbnailUrl: Safe.str,
    webVideoUrl: Safe.str,
    template: Safe.optional(Safe.str),
  }),
  Safe.Result.serializer({
    ok: Safe.obj({ subject: Safe.str, body: Safe.str }),
    error: Safe.str,
  })
);

export const renderTextEmailPreview = makeRoute(
  "/admin/api/plugin/send_a_message/text_email_preview",
  [],
  Safe.obj({
    footer: Safe.str,
    memberMessage: Safe.str,
    memberEmail: Safe.str,
    memberName: Safe.str,
    memberPostcode: Safe.str,
    line1: Safe.str,
    memberTown: Safe.str,
    targetName: Safe.str,
    template: Safe.optional(Safe.str),
  }),
  Safe.Result.serializer({
    ok: Safe.obj({ subject: Safe.str, body: Safe.str }),
    error: Safe.str,
  })
);

export const submissionTargetVideo = makeRoute(
  "/admin/api/plugin/send_a_message/submission_target_video",
  "public",
  Safe.obj({
    submission_guid: Safe.uuid,
  }),
  Safe.Result.serializer({
    ok: Safe.obj({ video_url: Safe.str }),
    error: Safe.str,
  })
);
